import FieldHeader, { type FieldHeaderProps } from '../../FieldHeader';
import React from 'react';
import { fieldVariants, type FieldVariants } from '../../../utils/forms/field';
import styles from './Field.module.css';
import FieldError from '../../FieldError';
import { useDatePickerContext } from '../../DatePicker/DatePickerContext';
import { altoConsole } from '../../../utils/console/console';
import { FormValidationContext } from 'react-stately';
import { resolveValidationState } from '../../../utils/forms/resolveValidationState';

export interface FieldCommonProps
  extends Omit<FieldHeaderProps, 'errorId' | 'showError'> {
  /**
   * Whether the Field is disabled
   */
  isDisabled?: boolean;
  /**
   * The number of columns the Field should have
   */
  columns?: FieldVariants['columns'];
  /**
   * Field width based on a rough estimate of the number of characters
   */
  maxWidthByChars?: FieldVariants['maxWidthByChars'];
  /**
   * The placement of the error message
   * @default 'top'
   */
  errorPlacement?: 'top' | 'bottom';
  /**
   * The label of the field. Required for accessibility compliance
   */
  label?: string;
  /**
   * The translation for the optional text, used for localization
   * @default 'Optional'
   */
  optionalTranslation?: string;
  /**
   * The id of the error message element
   */
  errorId?: string;
  /**
   * Prefix text for screen readers only in error messages
   * @default 'Error:'
   */
  errorPrefix?: string;
}

export type FieldProps<T extends object = object> = T & FieldCommonProps;

type FieldBaseProps<T extends object> = FieldProps<T> &
  Pick<FieldVariants, 'hasEndIcon' | 'hasStartIcon'> & {
    /**
     * The component or HTML element to render as the field container
     */
    as: React.ElementType;
    /**
     * The content of the Field component
     */
    children: React.ReactNode;
    /**
     * Optional className to be applied to the Field container
     */
    className?: React.ComponentProps<React.ElementType>['className'];
  };

function PolymorphicField<T extends object>({
  as = 'div',
  children,
  ...props
}: FieldBaseProps<T>) {
  return React.createElement(as, props, children);
}

export function Field<T extends object>({
  as = 'div',
  children,
  className,
  columns,
  maxWidthByChars,
  hasStartIcon,
  hasEndIcon,
  errorPlacement = 'top',
  label,
  labelId,
  labelFor,
  description,
  descriptionId,
  isRequired,
  optionalTranslation,
  isDisabled,
  errorMessage,
  isInvalid,
  errorId,
  errorPrefix,
  visuallyHiddenLabel,
  ...restProps
}: FieldBaseProps<T>) {
  const { isInDateRangePicker } = useDatePickerContext();
  const formValidationErrors = React.useContext(FormValidationContext);
  const rawName = (restProps as { name?: unknown }).name;
  const fieldName = typeof rawName === 'string' ? rawName : undefined;
  const { effectiveErrorMessage, effectiveIsInvalid } = resolveValidationState({
    isInvalid,
    errorMessage,
    fieldName,
    validationErrors: formValidationErrors,
  });

  if (!label && !visuallyHiddenLabel && !isInDateRangePicker) {
    altoConsole.warn(
      '`label` or `visuallyHiddenLabel` is expected for accessibility compliance',
      {
        scope: 'Field',
      },
    );
  }

  return (
    <PolymorphicField
      as={as}
      isDisabled={isDisabled}
      className={className}
      errorMessage={effectiveErrorMessage}
      isInvalid={effectiveIsInvalid}
      {...restProps}
    >
      {(label || description || errorPlacement === 'top') &&
      !isInDateRangePicker ? (
        <FieldHeader
          label={label}
          labelId={labelId}
          labelFor={labelFor}
          description={description}
          descriptionId={descriptionId}
          isRequired={isRequired}
          optionalTranslation={optionalTranslation}
          visuallyHiddenLabel={visuallyHiddenLabel}
          errorMessage={effectiveErrorMessage}
          isInvalid={effectiveIsInvalid}
          errorId={errorId}
          errorPrefix={errorPrefix}
          showError={errorPlacement === 'top'}
        />
      ) : null}
      <div className={isInDateRangePicker ? '' : styles.fieldGrid}>
        <div
          className={fieldVariants({
            columns,
            maxWidthByChars,
            hasStartIcon,
            hasEndIcon,
          })}
        >
          {children}
        </div>
      </div>
      {errorPlacement === 'bottom' ? (
        <div className={styles.fieldError}>
          <FieldError id={errorId} errorPrefix={errorPrefix}>
            {effectiveErrorMessage}
          </FieldError>
        </div>
      ) : null}
    </PolymorphicField>
  );
}

export default Field;
